import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { rememberSupportRef, reportUserError, supportRefFromEventId } from "@/lib/supportRef";

/**
 * What the user sees when a page fails to render (route error boundary and
 * the app-wide boundary). Controlled copy and a support reference; the error
 * itself goes to Sentry under that reference (FIX-LOG 93). Never a dead end:
 * reload, go back, or go to the dashboard.
 */
export function ErrorScreen({
  error,
  surface = "route_error",
  eventId,
}: {
  error: unknown;
  surface?: string;
  /** Set when a Sentry ErrorBoundary already captured it: reuse, don't re-report. */
  eventId?: string;
}) {
  const [ref, setRef] = useState<string | undefined>();

  useEffect(() => {
    if (eventId) {
      const r = supportRefFromEventId(eventId);
      rememberSupportRef(r, eventId);
      setRef(r);
      return;
    }
    setRef(reportUserError(error, { surface }));
  }, [error, surface, eventId]);

  return (
    <Box role="alert" sx={{ maxWidth: 560, mx: "auto", mt: 10, px: 2 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Something went wrong on this page
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        We've been notified. Reload the page to try again, or go back to where you were.
        {ref ? " If you contact support, quote the reference below." : ""}
      </Typography>
      {ref && (
        <Typography variant="body2" sx={{ fontFamily: "monospace", my: 2 }} data-testid="support-ref">
          Reference {ref}
        </Typography>
      )}
      <Stack direction="row" spacing={1} mt={2}>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Reload page
        </Button>
        <Button variant="outlined" onClick={() => window.history.back()}>
          Go back
        </Button>
        <Button variant="text" href="/">
          Go to dashboard
        </Button>
      </Stack>
    </Box>
  );
}
